import { driver, Driver, Side } from "driver.js";
import "driver.js/dist/driver.css";

export interface AdapterStepConfig {
  targetSelector: string;
  title: string;
  description: string;
  side?: Side;
}

export interface AdapterCallbacks {
  onStepActive?: (index: number) => void;
  onFinish?: () => void;
  onSkip?: () => void;
}

// TourAdapter abstracts the tour library away from the useTutorial hook so a
// different implementation (intro.js, Shepherd.js, custom overlay) can be
// dropped in without touching call sites.
export interface TourAdapter {
  start(steps: AdapterStepConfig[], callbacks: AdapterCallbacks): void;
  destroy(): void;
}

export function createDriverJsAdapter(): TourAdapter {
  let instance: Driver | null = null;

  return {
    start(steps, callbacks) {
      let finished = false;

      const totalSteps = steps.length;

      instance = driver({
        showProgress: true,
        allowClose: true,
        showButtons: ["next", "previous", "close"],
        onHighlightStarted: (_element, _step, opts) => {
          const idx = opts.index;
          if (typeof idx === "number") {
            callbacks.onStepActive?.(idx);
          }
        },
        onNextClick: (_element, _step, opts) => {
          const idx = opts.index ?? 0;
          if (idx >= totalSteps - 1) {
            finished = true;
            callbacks.onFinish?.();
            opts.driver.destroy();
          } else {
            opts.driver.moveNext();
          }
        },
        onPrevClick: (_element, _step, opts) => {
          opts.driver.movePrevious();
        },
        onCloseClick: (_element, _step, opts) => {
          opts.driver.destroy();
        },
        onDestroyStarted: () => {
          if (!finished) {
            callbacks.onSkip?.();
          }
        },
        steps: steps.map((s) => ({
          element: s.targetSelector,
          popover: {
            title: s.title,
            description: s.description,
            side: s.side,
          },
        })),
      });
      instance.drive();
    },
    destroy() {
      instance?.destroy();
      instance = null;
    },
  };
}
